import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { assertNever } from '@kofno/piper';
import { whenGt } from '@execonline-inc/numbers';
import { just, Maybe } from 'maybeasy';
import { observer } from 'mobx-react';
import { emptyFragment } from '../../../../../../EmptyFragment';
import MulticheckInput from '../../../../../../Multicheck/MulticheckInput';
import { MulticheckStore } from '../../../../../../Multicheck/Store';
import { distanceOfTimeInWords } from '../../../../../../Time';
import { AlreadyTranslated, L, T } from '../../../../../../Translations';
import ActionMenu from '../../../../../ActionMenu';
import ClickableTooltip from '../../../../../ClickableTooltip';
import ModalStore from '../../../../../Modal/Store';
import Popover from '../../../../../People/Groups/Table/Popover';
import InvitationStore from '../../Store/InvitationStore';
import { Invitation, InvitationResource } from '../../Store/Types';
import { actionItems } from '../ActionItems';
import RegistrationLabel from '../RegistrationLabel';
import InactiveIcon from './InactiveIcon';

interface Props {
  store: InvitationStore;
  resource: InvitationResource;
  multicheckStore: MulticheckStore<InvitationResource>;
  modalStore: ModalStore;
}

const whenUserHasEnrollments = (totalEnrollments: number): Maybe<number> =>
  just(totalEnrollments).andThen(whenGt(0));

const formatDateForEnrolledOn = (date: Date): string =>
  date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

interface InvitationEmailCellProps {
  invitation: Invitation;
}

const DateAndTime = ({ date }: { date: Date }) => (
  <>
    <span className="mr-1">
      <L localizeable={date} format="short-month-day-year" />
    </span>
    <span className="mr-1">
      <L localizeable={date} format="time-of-day" />
    </span>
  </>
);

interface CommunicationNameProps {
  invitationId: number;
  name: Maybe<string>;
}

// The name of the email the time belongs to: the one sent, or the one coming.
const CommunicationName = ({ invitationId, name }: CommunicationNameProps) =>
  name
    .filter((publicName) => publicName.trim().length > 0)
    .map((publicName) => (
      <ClickableTooltip
        tooltipId={`communication-${invitationId}`}
        tooltipIcon={<InformationCircleIcon className="h-4 w-4" />}
      >
        <span className="font-medium">{publicName}</span>
      </ClickableTooltip>
    ))
    .getOrElse(emptyFragment);

// DVLP-8056: only a sent invitation email shows as an email date. A scheduled
// one says so, and someone added without an email reads "Not emailed".
function InvitationEmailCell({ invitation }: InvitationEmailCellProps) {
  const email = invitation.invitationEmail;
  switch (email.kind) {
    case 'sent':
      return (
        <div className="flex items-center gap-2">
          <div>
            <DateAndTime date={email.at} />
            <span className="text-sm">
              {'('}
              <T {...distanceOfTimeInWords(new Date(), email.at)} />
              {')'}
            </span>
          </div>
          <CommunicationName invitationId={invitation.id} name={email.communicationName} />
        </div>
      );
    case 'scheduled':
      return (
        <div className="flex items-center gap-2" data-intercom-target="invitation-email-scheduled">
          <div>
            <span className="mr-1">
              <T kind="Scheduled for" />
            </span>
            <DateAndTime date={email.at} />
          </div>
          <CommunicationName invitationId={invitation.id} name={email.communicationName} />
        </div>
      );
    case 'not-emailed':
      return (
        <span data-intercom-target="invitation-email-not-emailed">
          <T kind="Not emailed" />
        </span>
      );
    default:
      return assertNever(email);
  }
}

function Content({ store, resource, modalStore, multicheckStore }: Props) {
  const { payload } = resource;
  return (
    <>
      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
        <MulticheckInput
          store={multicheckStore}
          object={resource}
          id={payload.id}
          ariaLabel={`Select ${payload.name} (${payload.email})`}
        />
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 flex flex-row items-center">
        <span className="mr-2">{payload.name}</span>
        <InactiveIcon invitationStatus={payload.invitationStatus} />
        <RegistrationLabel registrationType={payload.registrationType} />
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{payload.email}</td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        <InvitationEmailCell invitation={payload} />
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {payload.invitationAcceptedDate
          .map((date) => (
            <>
              <span className="mr-1">
                <L localizeable={date} format="short-month-day-year" />
              </span>
              <span className="mr-1">
                <L localizeable={date} format="time-of-day" />
              </span>
              <span className="text-sm">
                {'('}
                <T {...distanceOfTimeInWords(new Date(), date)} />
                {')'}
              </span>
            </>
          ))
          .getOrElse(() => (
            <>--</>
          ))}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {whenUserHasEnrollments(payload.totalEnrollments)
          .map((count) => (
            <div className="flex flex-row">
              <Popover
                title={`${count}`}
                content={
                  <>
                    {payload.courseRegistration.map((courseRegistration, index) => (
                      <p className="mb-4 whitespace-break-spaces" key={index}>
                        <span className="font-bold px-gap-1">
                          <AlreadyTranslated content={courseRegistration.payload.publicName} />
                        </span>{' '}
                        (<AlreadyTranslated content={courseRegistration.payload.studentStatus} />
                        {courseRegistration.payload.enrolledOn
                          .map((enrolledOn) => (
                            <T
                              kind="; joined on <date/>"
                              date={<>{formatDateForEnrolledOn(new Date(enrolledOn))}</>}
                            />
                          ))
                          .getOrElse(emptyFragment)}
                        )
                      </p>
                    ))}
                  </>
                }
              />
            </div>
          ))
          .getOrElse(() => (
            <>--</>
          ))}
      </td>
      <td>
        <ActionMenu>{actionItems({ store, resource, modalStore })}</ActionMenu>
      </td>
    </>
  );
}

export default observer(Content);
